'use client';
import { isMaybeIncognitoComet } from '#pplx/frontend/apps/ask/comet-shared/cometUtils.js';
import { useLimitedFreeTierExperienceVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useLimitedFreeTierExperienceVariation.js';
import { useUserCapabilities } from '#pplx/frontend/apps/ask/queries/user-queries/useUserCapabilities.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn, useIsUserLoggedInLoading } from '#pplx/frontend/auth/userHooks.js';
import { useMemo } from 'react';
/**
 * Wrapper around the codegen'd Eppo variation hook for the
 * `limited-free-tier-experience` string flag. Skips assignment until the user is already rate-limited
 * (`remaining === 0`) AND passes every other eligibility gate. Everyone else
 * resolves to `'control'` without an assignment fetch
 */
export const useLimitedFreeTierExperience = ({ freeQueries, }) => {
    const isUserLoggedIn = useIsUserLoggedIn();
    const isUserLoggedInLoading = useIsUserLoggedInLoading();
    const { hasActiveSubscription } = useIsSubscribed();
    const { isGovernmentRequestOrigin } = useUserCapabilities();
    const isComet = isMaybeIncognitoComet();
    const freeQueriesRemaining = freeQueries?.remaining_detail.kind === 'exact'
        ? freeQueries.remaining_detail.remaining
        : undefined;
    // Resolve Eppo within one submit of zero so the 1 -> 0 query submit captures
    // the real variant at submit time and serves appropriate treatment upon submission of last query.
    const skipAssignment = isUserLoggedInLoading ||
        !isUserLoggedIn ||
        hasActiveSubscription ||
        !!isGovernmentRequestOrigin ||
        isComet ||
        freeQueriesRemaining === undefined ||
        freeQueriesRemaining > 1;
    const { variation, loading } = useLimitedFreeTierExperienceVariation({
        defaultValue: 'control',
        skip: skipAssignment,
    });
    return useMemo(() => ({
        // The codegen'd hook returns a bare `string`; the flag is configured to
        // only ever serve these three values, so we narrow here.
        variation: variation,
        loading,
    }), [variation, loading]);
};
//# sourceMappingURL=useLimitedFreeTierExperience.js.map